<template>
  <div v-if="receiptData" class="layer paymentReceipt notranslate">
    <button type="button" class="btnClose" @click="close"><span class="blind">X</span></button>
    <div class="inner">
      <div
        ref="receipt"
        class="receipt"
        :class="{table: ln === 'ko' || ln === 'ja' || ln === 'zh-CN'}">
        <h1 class="layerTitle">{{isPaid ? locale.card_receipt : locale.card_receipt_refunded}}</h1>
        <div class="formGroup">
          <h2>{{locale.order_payment_information}}</h2>
          <div class="row">
            <span class="label">{{locale.order_card_name}}</span>
            <div class="value">{{receiptData.paymentInfo.cardName}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_card_number}}</span>
            <div class="value">{{receiptData.paymentInfo.cardNumber}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_pay_method}}</span>
            <div class="value">{{receiptData.paymentInfo.payMethod}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_pay_auth_date}}</span>
            <div class="value">{{receiptData.paymentInfo.authDate}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_pay_type}}</span>
            <div class="value">{{paymentType(receiptData.paymentInfo.type)}}</div>
          </div>
          <div v-if="!isPaid && receiptData.paymentInfo.refundDate" class="row">
            <span class="label">{{locale.order_pay_refund_date}}</span>
            <div class="value">{{receiptData.paymentInfo.refundDate}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_pay_quota}}</span>
            <div class="value">{{cardQuota(receiptData.paymentInfo.cardQuota)}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_pay_auth_code}}</span>
            <div class="value">{{receiptData.paymentInfo.authCode}}</div>
          </div>
        </div>
        <div class="formGroup">
          <h2>{{locale.order_purchase_information}}</h2>
          <div class="row">
            <span class="label">{{locale.order_number}}</span>
            <div class="value">{{receiptData.goodsInfo.orderId}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_amount}}</span>
            <div class="value">{{priceFormat(receiptData.goodsInfo.amt)}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_product_name}}</span>
            <div class="value">{{productName}}</div>
          </div>
        </div>
        <div class="formGroup">
          <h2>{{locale.order_shop_information}}</h2>
          <div class="row">
            <span class="label">{{locale.order_shop_name}}</span>
            <div class="value">{{receiptData.shopInfo.shopName}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.order_shop_biz_number}}</span>
            <div class="value">{{receiptData.shopInfo.shopBizNumber}}</div>
          </div>
          <div class="row">
            <span class="label">{{locale.address}}</span>
            <div class="value">{{receiptData.shopInfo.shopAddress}}</div>
          </div>
        </div>
      </div>
      <div class="btns">
        <button type="button" class="btn btnRoud btnFull" @click="generateReport(false)">{{locale.save_as_image}}</button>
        <button type="button" class="message mt10" @click="newWindowImage">{{locale.message_save_as_image}}</button>
      </div>
    </div>
    <div v-if="receiptImage" class="layer receiptImage">
      <div class="inner">
        <button type="button" class="btnClose" @click="receiptImage = null"><span class="blind">close</span></button>
        <img :src="receiptImage.src" alt="" />
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import regExp from '@/common/regExp'
import html2pdf from 'html2pdf.js'

export default {
  name: 'PaymentReceipt',
  props: [
    'lang',
    'locale'
  ],
  computed: {
    ln () {
      return this.$route.params.ln
    },
    currencyCode () {
      return this.$store.getters['common/getSetting'].currency.toUpperCase()
    },
    format () {
      const setting = this.$store.state.common.setting
      const date = setting.dateFormat[setting.dateformatDefault]
      const time = 'HH:mm:ss'
      return `${date} ${time}`
    },
    timezone () {
      return this.$store.state.siteInfo.timezoneId || ''
    },
    productName () {
      const goods = this.receiptData.goodsInfo.details
      const cnt = goods.length - 1
      const mainGoods = goods[0]
      const title = mainGoods.submitId.includes('system_amenity')
        ? this.locale.amenity
        : mainGoods.title
      const message = this.locale.order_product_count.replace('{var}', cnt) // 외 {var}건
      return cnt > 0 ? title + message : title
    },
    isPaid () {
      return this.receiptData.paymentInfo.type === 'paid'
    },
    htmlToPdfOptions () {
      return {
        margin: 1,
        filename: `${this.receiptData.goodsInfo.orderId}_Receipt`,
        image: {
          type: 'jpeg',
          quality: 0.98
        },
        enableLinks: false,
        html2canvas: {
          scrollY: 0,
          scale: 1,
          useCORS: true
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait'
        }
      }
    }
  },
  created () {
    const orderId = this.$route.params.orderId
    this.setReceipt(orderId)
  },
  data () {
    return {
      receiptImage: null,
      receiptData: null
    }
  },
  methods: {
    setReceipt (orderId) {
      if (orderId) {
        this.$store.dispatch('payment/getReceipt', orderId).then((res) => {
          const payload = Array.isArray(res.payload) ? res.payload : new Array(res.payload)
          // 결제정보
          const paymentInfo = {
            cardName: res.cardName || '-', // 카드종류
            cardNumber: res.cardNumber || '-', // 카드번호
            payMethod: res.payMethod || '-', // 거래종류
            authDate: res.authDate || '-', // 거래일시
            cardQuota: res.cardQuota || 0, // 할부개월
            authCode: res.authCode || '-',
            type: res.type, // 거래유형
            refundDate: res.refundDate // 취소일시
          }
          // 구매정보
          const goodsInfo = {
            orderId: orderId, // 주문번호
            details: payload,
            amt: res.amt
          }
          const shopInfo = {
            shopName: res.shopName || '-', // 상호명
            shopBizNumber: res.shopBizNumber || '-', // 사업자등록번호
            shopAddress: res.shopAddress || '-' // 주소
          }
          // 이용상점 정보
          this.receiptData = {
            paymentInfo: paymentInfo,
            goodsInfo: goodsInfo,
            shopInfo: shopInfo
          }
        })
      } else {
        this.receiptData = null
      }
    },
    paymentType (type) {
      return type === 'paid' ? this.locale.order_status_paid : this.locale.order_status_refunded
    },
    datetimeFormat (iso) {
      const datetime = DateTime.fromISO(iso, { zone: this.timezone })
      return datetime.toFormat(this.format)
    },
    cardQuota (quota) {
      const quot = Number(regExp.numberOnly(quota))
      return quot === 0 ? this.locale.order_pay_lump_sum : `${quot}${this.locale.order_pay_month}`
    },
    priceFormat (price) {
      const code = this.currencyCode
      return regExp.priceFormat(price, code)
    },
    generateReport (isDirectly) {
      const opt = this.htmlToPdfOptions
      const el = this.$refs.receipt
      const fileName = `${opt.filename}.png`
      html2pdf().set(opt).from(el).outputImg('dataurlstring').then((res) => {
        const a = document.createElement('a')
        a.href = res
        a.download = fileName
        if (isDirectly) {
          this.receiptImage = {
            src: res,
            fileName: fileName
          }
        } else {
          a.click()
        }
      })
    },
    newWindowImage () {
      // 일부 기기와 브라우저에서 이미지가 제대로 저장되지 않을 수 있습니다.
      // 이미지를 직접 다운로드 하시겠습니까?
      if (window.confirm(`${this.locale.order_receipt_message_1}\n${this.locale.order_receipt_message_2}`)) {
        this.generateReport(true)
      }
    },
    close () {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.paymentReceipt {
  z-index: 9999999;
  .inner {
    position: relative !important;
    padding: 0;
  }
  .receipt{
    padding: 15px 15px 0;
  }
  .btns {
    display: block;
    padding: 0 15px 45px;
    .message {
      position: relative;
      width: 100%;
      padding: 0.3em 1em;
      text-align: center;
      font-size: 0.85em;
      border-radius: 50px;
      background-color: $color-lgrey2;
      opacity: 0.75;
      &:before {
        content: "?";
        display: inline-block;
        position: relative;
        top: -1px;
        width: 14px;
        height: 14px;
        margin-right: 4px;
        line-height: 14px;
        font-family: 'sans-serif';
        font-size: 10px;
        color: $color-white;
        border-radius: 100%;
        background-color: $color-dark;
      }
      &:after {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 50%;
        width: 8px;
        height: 8px;
        margin-bottom: -4px;
        background-color: $color-lgrey2;
        transform: rotate(45deg);
      }
    }
  }
}
.loadingMessage {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  padding: 15px;
  text-align: center;
  font-size: 1.2em;
  color: $color-white;
  background-color: rgba($color-primary, 0.75);
  transform: translate(0, -50%);
}
.receiptImage {
  padding: 8px;
  .inner {
    padding: 15px;
  }
  .btnClose {
    background-color: $color-white;
    background-size: 20px;
  }
}
.formGroup {
  h2 {
    margin-bottom: 0.5em;
    font-weight: bold;
  }
  padding-top: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $color-lgrey2;
  &:first-of-type {
    border-top: 1px solid $color-dark;
  }
  .row {
    padding-top: 0.3em;
    padding-bottom: 0.3em;
    .label,
    .value {
      display: inline;
    }
    .label:after {
      content: ":";
      margin-left: 0.1em;
      margin-right: 0.3em;
    }
  }
}
.table {
  .row {
    display: flex;
    .value {
      flex: 1;
    }
    .label {
      width: 110px;
      margin-bottom: 0;
    }
    .label:after {
      display: none;
    }
  }
}
.displayTitle {
  font-size: 0.8em;
  color: $color-lgrey;
}
</style>
